<ng-container *appTranslationModule="'tasks'">
  <section
    class="overflow-hidden rounded-xl bg-white text-slate-900 shadow-sm"
    [attr.aria-label]="'crm_tasks.today.title' | translate"
  >
    <header class="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
      <span class="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-orange-50 text-orange-600">
        <app-icon>today</app-icon>
      </span>
      <h2 class="m-0 flex min-w-0 flex-1 items-center gap-2 text-base font-semibold leading-6 text-slate-950">
        <span class="truncate">{{ 'crm_tasks.today.title' | translate }}</span>
        @if (!loading && !failed) {
          <span class="rounded-full bg-slate-100 px-2 text-xs font-semibold text-slate-500">{{ total }}</span>
        }
      </h2>
      <a
        class="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg px-2 text-sm font-medium !text-slate-600 !no-underline transition hover:bg-slate-100"
        [routerLink]="tasksUrl"
      >
        {{ 'crm_tasks.today.all_tasks' | translate }}
        <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">arrow_forward</app-icon>
      </a>
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
        [matTooltip]="'crm_tasks.today.create' | translate"
        [attr.aria-label]="'crm_tasks.today.create' | translate"
        (click)="create()"
      >
        <app-icon>add</app-icon>
      </button>
    </header>

    <div class="crm-scroll max-h-80 overflow-y-auto">
      @if (loading) {
        <div class="flex flex-col gap-2 p-4">
          @for (row of [1, 2, 3]; track row) {
            <div class="h-12 animate-pulse rounded-lg bg-slate-100"></div>
          }
        </div>
      } @else if (failed) {
        <div class="flex min-h-40 flex-col items-center justify-center gap-3 px-4 py-8 text-center">
          <app-icon class="!text-[32px] text-slate-300">error_outline</app-icon>
          <p class="m-0 text-sm text-slate-600">{{ 'crm_tasks.today.load_failed' | translate }}</p>
          <app-button btnType="stroked" (onChangeClick)="load()">{{ 'crm_tasks.form.retry' | translate }}</app-button>
        </div>
      } @else if (!shown) {
        <div class="flex min-h-40 flex-col items-center justify-center gap-2 px-4 py-8 text-center">
          <app-icon class="!text-[32px] text-emerald-400">task_alt</app-icon>
          <p class="m-0 text-sm font-medium text-slate-700">{{ 'crm_tasks.today.empty' | translate }}</p>
          <p class="m-0 text-xs text-slate-400">{{ 'crm_tasks.today.empty_hint' | translate }}</p>
        </div>
      } @else {
        @for (group of groups; track group.key) {
          <div
            class="sticky top-0 z-10 flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide"
            [ngClass]="group.key === 'overdue' ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-500'"
          >
            @if (group.key === 'overdue') {
              <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">alarm</app-icon>
            }
            {{ (group.key === 'overdue' ? 'crm_tasks.today.overdue' : 'crm_tasks.today.due_today') | translate }}
            <span class="font-semibold opacity-70">{{ group.tasks.length }}</span>
          </div>
          @for (task of group.tasks; track task.id) {
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-3 px-4 py-2.5 text-left transition"
              [ngClass]="task.is_overdue ? 'hover:bg-rose-50' : 'hover:bg-slate-50'"
              (click)="open(task)"
            >
              <span class="min-w-0 flex-1">
                <span class="flex min-w-0 items-center gap-1.5">
                  @if (task.priority === 'HIGH') {
                    <app-icon
                      class="!h-4 !w-4 shrink-0 !text-[16px] !leading-none text-rose-500"
                      [matTooltip]="'crm_tasks.priority.HIGH' | translate"
                    >
                      arrow_upward
                    </app-icon>
                  }
                  <span class="truncate text-sm font-semibold text-slate-900" [title]="task.title">{{
                    task.title
                  }}</span>
                </span>
                <span class="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                  <span
                    class="inline-flex items-center gap-1"
                    [ngClass]="{ 'font-semibold text-rose-600': task.is_overdue }"
                  >
                    <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">{{
                      task.is_overdue ? 'alarm' : 'schedule'
                    }}</app-icon>
                    {{ task.deadline | date: (isSameDay(task.deadline) ? 'HH:mm' : 'dd.MM HH:mm') }}
                  </span>
                  @if (task.status === 'REVIEW') {
                    <span class="font-medium text-violet-600">{{ 'crm_tasks.today.awaits_review' | translate }}</span>
                  }
                  @if (task.assignee_id !== currentUserId) {
                    <app-task-user class="max-w-40" size="16px" [user]="getUser(task.assignee_id)" />
                  }
                </span>
              </span>
              <app-task-status-badge [status]="task.status" />
            </button>
          }
        }
        @if (total > shown) {
          <p class="m-0 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
            {{ 'crm_tasks.today.more' | translate: { shown: shown, total: total } }}
          </p>
        }
      }
    </div>
  </section>
</ng-container>
